import { FC, useState } from 'react';
import {
	Button,
	Card,
	Col,
	Flex,
	Input,
	Row,
	Select,
	Space,
	Table,
	message,
	Popconfirm,
} from 'antd';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
	getBouwheerList,
	deleteBouwheer,
	Bouwheer,
	bouwheerKeys,
} from '@/libs/fleet-api/services/admin-bouwheer';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import FleetTitle from '@/libs/antd/components/Title';
import BouwheerFormModal from '@/pages/admin/modals/BouwheerFormModal';

const { Search } = Input;
const { Option } = Select;

const BouwheerListPage: FC = () => {
	const queryClient = useQueryClient();
	const [searchText, setSearchText] = useState('');
	const [searchBy, setSearchBy] = useState<'name' | 'id'>('name');
	const [currentPage, setCurrentPage] = useState(1);
	const [pageSize, setPageSize] = useState(10);
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [modalMode, setModalMode] = useState<'create' | 'update'>('create');
	const [selectedBouwheer, setSelectedBouwheer] = useState<
		Bouwheer | undefined
	>(undefined);

	const { data: bouwheerData, isLoading } = useQuery({
		queryKey: bouwheerKeys.list({
			page: currentPage,
			pageSize: pageSize,
			search: { by: searchBy, value: searchText },
		}),
		queryFn: () =>
			getBouwheerList({
				page: currentPage,
				pageSize: pageSize,
				search: { by: searchBy, value: searchText },
			}),
	});

	const deleteMutation = useMutation({
		mutationFn: deleteBouwheer,
		onSuccess: () => {
			message.success('Bouwheer deleted successfully');
			queryClient.invalidateQueries({ queryKey: bouwheerKeys.all });
		},
		onError: (error) => {
			message.error('Failed to delete Bouwheer');
			console.error(error);
		},
	});

	const handleCreate = () => {
		setModalMode('create');
		setSelectedBouwheer(undefined);
		setIsModalOpen(true);
	};

	const handleEdit = (record: Bouwheer) => {
		setModalMode('update');
		setSelectedBouwheer(record);
		setIsModalOpen(true);
	};

	const handleDelete = (id: string) => {
		deleteMutation.mutate(id);
	};

	const columns = [
		{
			title: 'No',
			dataIndex: 'no',
			key: 'no',
			render: (_text: unknown, _record: unknown, index: number) =>
				(currentPage - 1) * pageSize + index + 1,
		},
		{
			title: 'ID',
			dataIndex: 'id',
			key: 'id',
		},
		{
			title: 'Name',
			dataIndex: 'name',
			key: 'name',
		},
		{
			title: 'Type',
			dataIndex: 'type',
			key: 'type',
		},
		{
			title: 'Industry Sector',
			key: 'industrySector',
			render: (_: unknown, record: Bouwheer) =>
				record.industrySector
					? `${record.industrySector.code} - ${record.industrySector.sectorLevel5}`
					: '-',
		},
		{
			title: 'Action',
			key: 'action',
			render: (_: unknown, record: Bouwheer) => (
				<Space>
					<Button
						type="link"
						icon={<EditOutlined />}
						onClick={() => handleEdit(record)}
					>
						Edit
					</Button>
					<Popconfirm
						title="Are you sure you want to delete this Bouwheer?"
						onConfirm={() => handleDelete(record.id)}
						okText="Yes"
						cancelText="No"
					>
						<Button
							type="link"
							danger
							icon={<DeleteOutlined />}
						>
							Delete
						</Button>
					</Popconfirm>
				</Space>
			),
		},
	];

	return (
		<div style={{ width: '100%' }}>
			<div
				style={{
					display: 'flex',
					justifyContent: 'space-between',
					alignItems: 'center',
					marginBottom: '24px',
				}}
			>
				<div style={{ width: '100%' }}>
					<FleetTitle level={1}>
						<Flex
							style={{ gap: 10 }}
							justify="space-between"
							align="center"
						>
							<span>Master Bouwheer</span>
							<Button
								type="primary"
								size="small"
								icon={<PlusOutlined />}
								onClick={handleCreate}
							>
								Add Bouwheer
							</Button>
						</Flex>
					</FleetTitle>
				</div>
			</div>

			<Card>
				<Row
					gutter={[16, 16]}
					style={{ marginBottom: '16px' }}
				>
					<Col
						xs={24}
						sm={8}
					>
						<Select
							value={searchBy}
							onChange={setSearchBy}
							style={{ width: '100%' }}
						>
							<Option value="name">Search by Name</Option>
							<Option value="id">Search by ID</Option>
						</Select>
					</Col>
					<Col
						xs={24}
						sm={16}
					>
						<Search
							placeholder="Search..."
							value={searchText}
							onChange={(e) => setSearchText(e.target.value)}
							onSearch={() => setCurrentPage(1)}
							allowClear
							enterButton
						/>
					</Col>
				</Row>

				<Table
					columns={columns}
					dataSource={bouwheerData?.data?.items}
					loading={isLoading}
					rowKey="id"
					pagination={{
						current: currentPage,
						pageSize: pageSize,
						total: bouwheerData?.data?.totalCount,
						onChange: (page, size) => {
							setCurrentPage(page);
							setPageSize(size);
						},
						showSizeChanger: true,
					}}
					scroll={{ x: 'max-content' }}
				/>
			</Card>

			<BouwheerFormModal
				open={isModalOpen}
				onClose={() => setIsModalOpen(false)}
				mode={modalMode}
				initialValues={selectedBouwheer}
			/>
		</div>
	);
};

export default BouwheerListPage;
